import React, { useState } from 'react';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';

import { formatBytes } from '../utilities';
import { TrashIcon } from '../icons';
import { OperationResultClass } from '../proxy/DataServiceEntities.g';

interface IProps {
    id: string;
    fileTypes: string;
    maxSize: number;
    buttonText?: string;
    isDisabled: boolean;
    uploadFile: (file: File) => Promise<string>;
    deleteFile: (fileUrl: string) => Promise<OperationResultClass>;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const FileUploader = ({ id, fileTypes, maxSize, buttonText = 'Upload', isDisabled, uploadFile, deleteFile, onChange }: IProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadedFileLink, setUploadedFileLink] = useState<string>('');
    const [file, setFile] = useState<File | null>(null);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (!file) {
            return;
        }

        if (file.size > maxSize) {
            setError(`The maximum allowed file size is ${formatBytes(maxSize)}. Actual size: ${formatBytes(file.size)}.`);

            return;
        }

        const allowedFileTypes = new RegExp(`(${fileTypes.replace(/,/g, '|')})$`, 'i');

        if (!allowedFileTypes.test(file.name)) {
            setError(`Invalid file format. Please upload a file in one of the following formats: ${fileTypes}`);
            setUploadedFileLink('');

            return;
        }

        setFile(file);
        setError(null);
        setIsLoading(true);

        try {
            const link = await uploadFile(file);

            if (link) {
                setUploadedFileLink(link);
                onChange({ target: { id, value: link } } as React.ChangeEvent<HTMLInputElement>);
            }
        } catch (e) {
            setError('File upload failed. Please try again');
            setFile(null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteFile = async () => {
        setIsLoading(true);

        try {
            await deleteFile(uploadedFileLink);
        } catch (e) {
            console.log('Error: ', e);
        } finally {
            setIsLoading(false);
            setUploadedFileLink('');
            setFile(null);
            onChange({ target: { id, value: '' } } as React.ChangeEvent<HTMLInputElement>);
        }
    };

    if (isLoading) {
        return (
            <div className='file-uploader-uploading-status-container'>
                <Spinner size={SpinnerSize.medium} />
                <p className='file-uploader-uploading-status-text'>In progress... Please wait.</p>
            </div>
        );
    }

    return (
        <div>
            {uploadedFileLink ? (
                <div>
                    {!error && <p className='file-uploader-success-text'>File uploaded successfully!</p>}
                    <div className='file-uploader-uploaded-file-container'>
                        <a href={uploadedFileLink} download>
                            {file?.name}
                        </a>
                        <button className='file-uploader-delete-file-link-button' onClick={handleDeleteFile}>
                            <TrashIcon />
                        </button>
                    </div>
                </div>
            ) : (
                <div className='file-uploader-button-container'>
                    <button
                        className={`file-uploader-button ${isLoading || isDisabled ? 'file-uploader-button-disabled' : ''}`}
                        disabled={isDisabled}
                    >
                        {buttonText}
                    </button>
                    <input
                        className='file-uploader-input'
                        type='file'
                        id={id}
                        value={uploadedFileLink}
                        accept={fileTypes}
                        onChange={handleFileChange}
                        disabled={isLoading || isDisabled}
                    />
                    {!error && <div>{`Max file size: ${formatBytes(maxSize)}. Formats: ${fileTypes}`}</div>}
                </div>
            )}

            {error && <p className='file-uploader-error-text'>{error}</p>}
        </div>
    );
};
